import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip,
  InputAdornment,
  Snackbar,
  Switch,
  FormControlLabel,
  FormHelperText,
  ListItemButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import ExtensionAttributesTab from './extensions/ExtensionAttributesTab';
import ADDirectoryExtensionsManager from "./extensions/ADDirectoryExtensionsManager";

// Define interfaces for extension types
interface ExtensionProperty {
  name: string;
  type: string;
  isMultiValued?: boolean;
}

interface DirectoryExtension {
  id: string;
  name?: string;
  targetObjects?: string[];
  properties?: ExtensionProperty[];
}

// Define the main component
const SchemaExtensionsTool: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState<boolean>(true);
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Note: DirectoryExtensionsManager functionality removed; keeping only shared state
  
  // Notifications
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info");

  // Removed app-owned directory extension management state
  
  // Error handling
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Get Graph client (read-only)
  const getGraphClient = useCallback(async () => {
    try {
      if (!accounts || accounts.length === 0) {
        console.error("getGraphClient: No account available.");
        throw new Error("No active account found.");
      }
      const response = await instance.acquireTokenSilent({
        scopes: ["Directory.Read.All"],
        account: accounts[0],
      });

      return Client.init({
        authProvider: (done) => {
          done(null, response.accessToken);
        }
      });
    } catch (error: any) {
      console.error("Error acquiring token for Graph client:", error);
      // Provide clearer guidance for consent errors without triggering an account picker
      if (error?.name === 'InteractionRequiredAuthError' || error?.errorCode === 'consent_required') {
        const username = accounts?.[0]?.username;
        const tenantId = accounts?.[0]?.tenantId;
        const appName = 'AppConfig';
        setErrorMessage(`Permissions missing for ${appName}. Please grant admin consent in the signed-in tenant${tenantId ? ` (${tenantId})` : ''} for this user${username ? ` (${username})` : ''}. Then reopen this tool.`);
      }
      throw error;
    }
  }, [instance, accounts]);
  
  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setSearchQuery("");
  };

  useEffect(() => {
    const verifyPermissionsAndFetch = async () => {
      setPermissionError(false);
      setErrorMessage("");
  
      if (!accounts || accounts.length === 0) {
        setLoading(false);
        return;
      }
  
      try {
        setLoading(true);
        await instance.acquireTokenSilent({
          scopes: ["Directory.Read.All"],
          account: accounts[0]
        });
  
        setPermissionError(false);
  
        // Read-only mode: no prefetch needed here
        setLoading(false);
      } catch (error: any) {
        console.error("Permission verification or data fetch failed:", error);
        if (error.name === 'InteractionRequiredAuthError' || error.name === 'ConsentRequired' || 
            error.errorCode === 'consent_required' || error.errorCode === 'login_required') {
          setPermissionError(true);
          setErrorMessage("Required permissions not granted or interaction needed. Please ensure admin consent is provided.");
        } else if (error instanceof Error && error.message === "No active account found.") {
          setErrorMessage("No active user session. Please sign in again.");
          setPermissionError(true);
        } else {
          if (!errorMessage) {
            setErrorMessage(`Failed to load data: ${error.message || "Unknown error"}`);
          }
        }
        setLoading(false);
      }
    };
  
    verifyPermissionsAndFetch();
  }, [accounts, instance]);

  // Offer an inline way to grant consent as the signed-in admin (avoids account picker)
  const handleGrantConsent = async () => {
    try {
      setLoading(true);
      if (!accounts || accounts.length === 0) throw new Error('No active account');
      const current = accounts[0];
      const baseAuth = instance.getConfiguration().auth.authority || 'https://login.microsoftonline.com/common';
      const tenantId = current.tenantId;
      const authority = (() => {
        try {
          const u = new URL(baseAuth);
          const parts = u.pathname.split('/').filter(Boolean);
          if (parts.length > 0 && tenantId) {
            parts[parts.length - 1] = tenantId;
            u.pathname = '/' + parts.join('/');
          }
          return u.toString();
        } catch {
          return baseAuth;
        }
      })();

      await instance.acquireTokenPopup({
        scopes: ["Directory.Read.All"],
        account: current,
        loginHint: current.username,
        authority,
        prompt: 'consent'
      });

      setPermissionError(false);
      setErrorMessage("");
      setSnackbarMessage('Consent granted. You can proceed.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e: any) {
      console.error('Consent flow failed:', e);
      setErrorMessage(e?.message || 'Consent failed');
    } finally {
      setLoading(false);
    }
  };

  // Function to copy extension ID to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setSnackbarMessage("Copied to clipboard");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      },
      () => {
        setSnackbarMessage("Failed to copy to clipboard");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    );
  };

  // All directory-extension write capabilities removed

  // Main content rendering
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Permission Error Check */}
      {permissionError ? (
        <Card variant="outlined" sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
          <WarningIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Permission Error
          </Typography>
          <Typography variant="body1" paragraph>
            The Claims Extensions Reference requires Directory.Read.All permission.
            Please ensure you've granted admin consent for this permission.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleGrantConsent}
              disabled={loading}
              startIcon={<ContentCopyIcon />}
              sx={{ mt: 1, borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Grant admin consent (this tenant)'}
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              onClick={onBack}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 1, borderRadius: 2 }}
            >
              Back to Tools
            </Button>
          </Box>
        </Card>
      ) : (
        <>        
          {/* Header Card */}
          <Card
            variant="outlined"
            sx={{
              mb: 4,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Header with Back button and title */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                backgroundColor: '#f4f8fb',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{
                  mr: 2,
                  borderRadius: 2,
                  pl: 1.5,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                    borderColor: "primary.main",
                    color: "primary.main"
                  }
                }}
              >
                Back
              </Button>
              
              <Box
                sx={{
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  py: 0.5,
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    letterSpacing: '-0.01em',
                  }}
                >
                  Claims Extensions Reference
                </Typography>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  }}
                >
                  View directory extensions and attributes for use in claims mapping policies
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Main Grid Layout */}
          <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              variant="outlined" 
              sx={{ 
                borderRadius: 2,
                height: '100%'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Extension Type Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary">
                  <Tab label="AD Directory Extensions" />
                  <Tab label="Extension Attributes" />
                </Tabs>
                                </Box>

                {/* Tab content */}
                <Box sx={{ p: 3 }}>
                  {/* AD Directory Extensions Tab - Now first (index 0) */}
                  {tabIndex === 0 && (
                    <ADDirectoryExtensionsManager
                      getGraphClient={getGraphClient}
                      copyToClipboard={copyToClipboard}
                      setErrorMessage={setErrorMessage}
                      setLoading={setLoading}
                      loading={loading}
                      setSnackbarMessage={setSnackbarMessage}
                      setSnackbarSeverity={setSnackbarSeverity}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  )}

                  {/* Extension Attributes Tab - Remains second (index 1) */}
                  {tabIndex === 1 && (
                    <ExtensionAttributesTab
                      getGraphClient={getGraphClient}
                      copyToClipboard={copyToClipboard}
                      setErrorMessage={setErrorMessage}
                      setLoading={setLoading}
                      loading={loading}
                    />
                  )}

                  {/* Directory Extensions tab removed (write operations not supported) */}
                </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSnackbarOpen(false)} 
              severity={snackbarSeverity}
              sx={{ width: '100%', borderRadius: 2 }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>

          {/* Error message */}
          {errorMessage && (
            <Snackbar
              open={!!errorMessage}
              autoHideDuration={6000}
              onClose={() => setErrorMessage("")}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert 
                onClose={() => setErrorMessage("")} 
                severity="error"
                sx={{ width: '100%', borderRadius: 2 }}
              >
                {errorMessage}
              </Alert>
            </Snackbar>
          )}
        </> 
      )}
    </Container>
  );
};

export default SchemaExtensionsTool;